import { IDataTableScrollPosition } from '@srm/ui/data-table';
import { IDataTableSavedState } from '@srm/ui/data-table/state';
import { compressToUint8Array, decompressFromUint8Array } from 'lz-string';
import { base64ToBytes, bytesToBase64 } from '@srm/ui/lib/base64';

export const GRID_STATE_PARAM = 'gridState';
const COMPRESSED_GRID_STATE_PREFIX = 'z';
const JSON_GRID_STATE_PREFIX = 'j';
const COLUMN_KEY_PREFIX = '~';
const COLUMN_INDEX_PREFIX = '#';
/** Версия wire-формата. Прежние форматы не читаются: устаревшая ссылка
 *  открывает папку с обычным начальным состоянием, это безопасная деградация. */
const WIRE_VERSION = 2;

/** Снимок таблицы для URL. Поля-дельты: отсутствие поля означает «как по
 * умолчанию у схемы», поэтому целиком снимок имеет смысл только вместе с
 * отпечатком схемы, против которой он снят. `wrapMode` в URL не хранится —
 * это личная настройка показа, она живёт в localStorage (wrapModeKey). */
export interface GridTableUrlState extends IDataTableSavedState {
    /** Отпечаток колонок схемы для безопасного восстановления #index и дельт. */
    columnSchemaKey?: string;
}

export interface IGridUrlState {
    version: typeof WIRE_VERSION;
    dataKey: string;
    predefinedFilterKey?: string;
    /** Сохранённый вид, поверх которого получен точный снимок table. */
    viewName?: string;
    table?: GridTableUrlState;
    scroll?: IDataTableScrollPosition;
}

/** Дефолты текущей схемы грида: снимок хранится дельтой против них. */
export interface IGridUrlSchema {
    /** Поля колонок в порядке схемы. */
    columnIds: string[];
    /** Колонки, скрытые схемой по умолчанию. */
    hiddenColumnIds: string[];
    /** Закрепление колонок по умолчанию. */
    pinnedLeftIds: string[];
    pinnedRightIds: string[];
    /** Сортировка схемы по умолчанию. */
    defaultSorting: { id: string; desc: boolean }[];
    defaultPageSize?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** Верхний предел числовых значений из адреса: skip/take уходят на сервер в int32. */
const MAX_SAFE_SERVER_INT = 2147483647;

const nonNegativeInteger = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= MAX_SAFE_SERVER_INT
        ? value
        : undefined;

const positiveInteger = (value: unknown): number | undefined => {
    const result = nonNegativeInteger(value);
    return result != null && result > 0 ? result : undefined;
};

const boundedString = (value: unknown, maxLength: number): string | undefined =>
    typeof value === 'string' && value.length <= maxLength ? value : undefined;

const stringArray = (value: unknown): string[] | undefined =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined;

/** Два независимых 32-bit hash вместо полного технического имени колонки.
 *  64 бит достаточно, чтобы collision между колонками одной схемы был
 *  практически невозможен, а URL не зависел от длины field path. Настоящие
 *  имена полей приходят из схем (`fields.…`) и с префиксов `~`/`#` не
 *  начинаются — инвариант, на котором держится развёртка коротких ключей. */
export const gridUrlColumnKey = (field: string): string => {
    if (field.startsWith(COLUMN_KEY_PREFIX) || field.startsWith(COLUMN_INDEX_PREFIX)) return field;
    let first = 0x811c9dc5;
    let second = 0x9e3779b9;
    for (let index = 0; index < field.length; index += 1) {
        const code = field.charCodeAt(index);
        first = Math.imul(first ^ code, 0x01000193);
        second = Math.imul(second ^ code, 0x85ebca6b);
        second = (second << 13) | (second >>> 19);
    }
    return `${COLUMN_KEY_PREFIX}${(first >>> 0).toString(36)}.${(second >>> 0).toString(36)}`;
};

const schemaFingerprint = (schema: IGridUrlSchema): string => gridUrlColumnKey(schema.columnIds.join('\u0000'));

const mapTableColumnIds = (
    state: GridTableUrlState,
    mapField: (field: string) => string | undefined,
): GridTableUrlState => {
    const mapFields = (fields: string[] | undefined): string[] | undefined =>
        fields?.map(mapField).filter((field): field is string => field != null);
    const mapRecord = <T>(record: Record<string, T> | undefined): Record<string, T> | undefined =>
        record
            ? Object.fromEntries(
                  Object.entries(record).flatMap(([field, value]) => {
                      const mapped = mapField(field);
                      return mapped == null ? [] : [[mapped, value]];
                  }),
              )
            : undefined;

    return {
        ...state,
        sorting: state.sorting?.flatMap((item) => {
            const id = mapField(item.id);
            return id == null ? [] : [{ ...item, id }];
        }),
        columnFilters: state.columnFilters?.flatMap((item) => {
            const id = mapField(item.id);
            return id == null ? [] : [{ ...item, id }];
        }),
        columnVisibility: mapRecord(state.columnVisibility),
        columnSizing: mapRecord(state.columnSizing),
        columnOrder: mapFields(state.columnOrder),
        columnPinning: state.columnPinning
            ? {
                  left: mapFields(state.columnPinning.left),
                  right: mapFields(state.columnPinning.right),
              }
            : undefined,
        grouping: mapFields(state.grouping),
    };
};

const compactGridTableUrlState = (state: GridTableUrlState): GridTableUrlState =>
    mapTableColumnIds(state, gridUrlColumnKey);

const sameStringArrays = (left: readonly string[], right: readonly string[]): boolean =>
    left.length === right.length && left.every((item, index) => item === right[index]);

const sameSorting = (
    sorting: readonly { id: string; desc: boolean }[],
    defaults: readonly { id: string; desc: boolean }[],
): boolean =>
    sorting.length === defaults.length &&
    sorting.every((item, index) => item.id === defaults[index].id && item.desc === defaults[index].desc);

const samePinning = (
    pinning: NonNullable<IDataTableSavedState['columnPinning']> | undefined,
    schema: IGridUrlSchema,
): boolean =>
    !pinning ||
    (sameStringArrays(pinning.left ?? [], schema.pinnedLeftIds) &&
        sameStringArrays(pinning.right ?? [], schema.pinnedRightIds));

/**
 * Готовит снимок таблицы к записи в URL: то, что совпадает с дефолтами схемы
 * (видимость, порядок, закрепление, сортировка, размер страницы), не пишется —
 * отсутствие поля при восстановлении приводит к тем же дефолтам. Оставшиеся
 * имена колонок заменяются индексами схемы `#0…`, отпечаток `columnSchemaKey`
 * не даёт применить и индексы, и дельты к изменившемуся набору колонок.
 */
export const compactGridTableUrlStateBySchema = (
    state: IDataTableSavedState,
    schema: IGridUrlSchema,
): GridTableUrlState => {
    const hiddenByScheme = new Set(schema.hiddenColumnIds);
    const visibilityDelta = state.columnVisibility
        ? Object.fromEntries(
              // хранится только отличие от схемы: value=true у схемно-скрытой
              // колонки (пользователь показал) или false у видимой (скрыл)
              Object.entries(state.columnVisibility).filter(
                  ([field, visible]) => visible === hiddenByScheme.has(field),
              ),
          )
        : undefined;
    const delta: GridTableUrlState = {
        sorting: state.sorting && !sameSorting(state.sorting, schema.defaultSorting) ? state.sorting : undefined,
        columnFilters: state.columnFilters?.length ? state.columnFilters : undefined,
        columnVisibility: visibilityDelta && Object.keys(visibilityDelta).length ? visibilityDelta : undefined,
        columnSizing: state.columnSizing && Object.keys(state.columnSizing).length ? state.columnSizing : undefined,
        columnOrder:
            state.columnOrder && !sameStringArrays(state.columnOrder, schema.columnIds)
                ? state.columnOrder
                : undefined,
        columnPinning: samePinning(state.columnPinning, schema) ? undefined : state.columnPinning,
        grouping: state.grouping?.length ? state.grouping : undefined,
        pageIndex: state.pageIndex ? state.pageIndex : undefined,
        pageSize: state.pageSize !== schema.defaultPageSize ? state.pageSize : undefined,
    };

    const indexes = new Map(
        schema.columnIds.map((field, index) => [field, `${COLUMN_INDEX_PREFIX}${index.toString(36)}`]),
    );
    return {
        ...mapTableColumnIds(delta, (field) => indexes.get(field) ?? gridUrlColumnKey(field)),
        columnSchemaKey: schemaFingerprint(schema),
    };
};

/**
 * Разворачивает снимок после получения схемы: индексы и хеши — обратно в имена
 * колонок, дельта видимости — поверх схемных дефолтов (потребителю нужна полная
 * карта: от неё зависит состав fields запроса). Несовпадение отпечатка означает,
 * что и индексы, и дельты принадлежат другой схеме — снимок не применяется
 * целиком, папка открывается с обычным начальным состоянием. Обычные field id
 * остаются как есть, поэтому helper безопасен и для initialTableState не из URL.
 */
export const restoreGridTableUrlState = (
    state: GridTableUrlState | undefined,
    schema: IGridUrlSchema,
): GridTableUrlState | undefined => {
    if (!state) return undefined;
    if (state.columnSchemaKey && state.columnSchemaKey !== schemaFingerprint(schema)) return undefined;

    const fieldsByKey = new Map(schema.columnIds.map((field) => [gridUrlColumnKey(field), field]));
    const restored = mapTableColumnIds(state, (field) => {
        if (field.startsWith(COLUMN_KEY_PREFIX)) return fieldsByKey.get(field);
        if (!field.startsWith(COLUMN_INDEX_PREFIX)) return field;
        const index = Number.parseInt(field.slice(COLUMN_INDEX_PREFIX.length), 36);
        return Number.isInteger(index) && index >= 0 ? schema.columnIds[index] : undefined;
    });
    delete restored.columnSchemaKey;
    if (restored.columnVisibility) {
        restored.columnVisibility = {
            ...Object.fromEntries(schema.hiddenColumnIds.map((field) => [field, false])),
            ...restored.columnVisibility,
        };
    }
    return restored;
};

/** Отбирает из снимка только сериализуемые поля — без подстановки пустышек:
 * отсутствие поля в URL само по себе значит «как по умолчанию у схемы». */
export const gridTableUrlState = (state: GridTableUrlState): GridTableUrlState => ({
    sorting: state.sorting,
    columnFilters: state.columnFilters,
    columnVisibility: state.columnVisibility,
    columnSizing: state.columnSizing,
    columnOrder: state.columnOrder,
    columnPinning: state.columnPinning,
    grouping: state.grouping,
    pageIndex: state.pageIndex,
    pageSize: state.pageSize,
    columnSchemaKey: state.columnSchemaKey,
});

const encodeBytesBase64Url = (bytes: Uint8Array): string =>
    bytesToBase64(bytes).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');

const decodeBytesBase64Url = (value: string): Uint8Array =>
    base64ToBytes(
        value
            .replaceAll('-', '+')
            .replaceAll('_', '/')
            .padEnd(Math.ceil(value.length / 4) * 4, '='),
    );

const decodeGridUrlStateJson = (value: string): string => {
    // SQL-authored links use the same v2 wire schema without an LZ-string dependency.
    if (value.startsWith(JSON_GRID_STATE_PREFIX)) {
        return new TextDecoder('utf-8', { fatal: true }).decode(decodeBytesBase64Url(value.slice(1)));
    }
    if (!value.startsWith(COMPRESSED_GRID_STATE_PREFIX)) throw new Error('Unsupported grid state format');
    const bytes = decodeBytesBase64Url(value.slice(COMPRESSED_GRID_STATE_PREFIX.length));
    const decompressed = decompressFromUint8Array(bytes);
    if (!decompressed) throw new Error('Empty compressed grid state');
    return decompressed;
};

// ----- Wire-формат: однобуквенные ключи и кортежи вместо самоописательного JSON.
// SQL-ссылки дашбордов также используют v2 (префикс j). Менять wire можно
// только вместе с WIRE_VERSION и документированным контрактом авторских ссылок.

/** Обрезает хвостовые null кортежа: JSON не умеет undefined в массиве. */
const trimTrailingNulls = (values: (string | number | null)[]): (string | number | null)[] => {
    let length = values.length;
    while (length > 0 && values[length - 1] == null) length -= 1;
    return values.slice(0, length);
};

const toWire = (state: IGridUrlState): Record<string, unknown> => {
    const table = state.table ? compactGridTableUrlState(state.table) : undefined;
    const wireTable = table
        ? {
              s: table.sorting?.map(({ id, desc }) => (desc ? [id, 1] : [id])),
              f: table.columnFilters?.map(({ id, value }) => [id, value]),
              v:
                  table.columnVisibility &&
                  Object.fromEntries(
                      Object.entries(table.columnVisibility).map(([field, visible]) => [field, visible ? 1 : 0]),
                  ),
              z: table.columnSizing,
              o: table.columnOrder,
              p: table.columnPinning && [table.columnPinning.left ?? [], table.columnPinning.right ?? []],
              g: table.grouping,
              i: table.pageIndex,
              l: table.pageSize,
              k: table.columnSchemaKey,
          }
        : undefined;
    return {
        v: WIRE_VERSION,
        d: state.dataKey,
        f: state.predefinedFilterKey,
        n: state.viewName,
        t: wireTable && Object.values(wireTable).some((value) => value !== undefined) ? wireTable : undefined,
        c: state.scroll
            ? trimTrailingNulls([
                  state.scroll.top,
                  state.scroll.left,
                  state.scroll.rowIndex ?? null,
                  state.scroll.rowOffset ?? null,
                  state.scroll.rowId ?? null,
              ])
            : undefined,
    };
};

const wireSorting = (value: unknown): GridTableUrlState['sorting'] =>
    Array.isArray(value)
        ? value.flatMap((item) => {
              if (!Array.isArray(item) || typeof item[0] !== 'string') return [];
              return [{ id: item[0], desc: item[1] === 1 }];
          })
        : undefined;

const wireFilters = (value: unknown): GridTableUrlState['columnFilters'] =>
    Array.isArray(value)
        ? value.flatMap((item) => {
              if (!Array.isArray(item) || typeof item[0] !== 'string' || item.length < 2) return [];
              return [{ id: item[0], value: item[1] as unknown }];
          })
        : undefined;

const wireVisibility = (value: unknown): GridTableUrlState['columnVisibility'] => {
    if (!isRecord(value)) return undefined;
    return Object.fromEntries(
        Object.entries(value).flatMap(([field, visible]) =>
            visible === 0 || visible === 1 ? [[field, visible === 1]] : [],
        ),
    );
};

const wireSizing = (value: unknown): GridTableUrlState['columnSizing'] => {
    if (!isRecord(value)) return undefined;
    return Object.fromEntries(
        Object.entries(value).filter(
            (entry): entry is [string, number] =>
                typeof entry[1] === 'number' && Number.isFinite(entry[1]) && entry[1] >= 0,
        ),
    );
};

const wireTableState = (value: unknown): GridTableUrlState | undefined => {
    if (!isRecord(value)) return undefined;
    const pinningRaw = Array.isArray(value.p) ? value.p : undefined;
    return {
        sorting: wireSorting(value.s),
        columnFilters: wireFilters(value.f),
        columnVisibility: wireVisibility(value.v),
        columnSizing: wireSizing(value.z),
        columnOrder: stringArray(value.o),
        columnPinning: pinningRaw
            ? { left: stringArray(pinningRaw[0]) ?? [], right: stringArray(pinningRaw[1]) ?? [] }
            : undefined,
        grouping: stringArray(value.g),
        pageIndex: nonNegativeInteger(value.i),
        pageSize: positiveInteger(value.l),
        columnSchemaKey: boundedString(value.k, 100),
    };
};

const wireScroll = (value: unknown): IDataTableScrollPosition | undefined => {
    if (!Array.isArray(value)) return undefined;
    const top = nonNegativeInteger(value[0]);
    const left = nonNegativeInteger(value[1]);
    if (top == null || left == null) return undefined;
    const rowIndex = nonNegativeInteger(value[2]);
    const rowOffset = nonNegativeInteger(value[3]);
    const rowId = boundedString(value[4], 2_000);
    return {
        top,
        left,
        ...(rowIndex != null ? { rowIndex } : {}),
        ...(rowOffset != null ? { rowOffset } : {}),
        ...(rowId != null ? { rowId } : {}),
    };
};

export const encodeGridUrlState = (state: IGridUrlState): string =>
    `${COMPRESSED_GRID_STATE_PREFIX}${encodeBytesBase64Url(compressToUint8Array(JSON.stringify(toWire(state))))}`;

export const readGridUrlState = (searchParams: URLSearchParams, dataKey: string): IGridUrlState | undefined => {
    const encoded = searchParams.get(GRID_STATE_PARAM);
    if (!encoded || encoded.length > 50_000) return undefined;

    try {
        const raw: unknown = JSON.parse(decodeGridUrlStateJson(encoded));
        if (!isRecord(raw) || raw.v !== WIRE_VERSION || raw.d !== dataKey) return undefined;

        return {
            version: WIRE_VERSION,
            dataKey,
            predefinedFilterKey: boundedString(raw.f, 500),
            viewName: boundedString(raw.n, 500),
            table: wireTableState(raw.t),
            scroll: wireScroll(raw.c),
        };
    } catch {
        return undefined;
    }
};

export const replaceGridUrlState = (state: IGridUrlState): void => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const encoded = encodeGridUrlState(state);
    if (url.searchParams.get(GRID_STATE_PARAM) === encoded) return;
    url.searchParams.set(GRID_STATE_PARAM, encoded);
    window.history.replaceState(window.history.state, '', url);
};
